import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { CONTACT_EMAIL, DATA_REPO_LINK } from "../constants";
import {
  communitySignals,
  researchOutputs,
  researchPrograms,
} from "../data/research";

const About = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        padding: { lg: "80px 0 125px", sm: "80px 0 125px", xs: "50px 0 100px" },
      }}
    >
      <Grid container spacing={{ lg: 5, xs: 3 }}>
        <Grid item md={12} xs={12}>
          <Typography
            sx={{
              fontSize: { lg: "96px", md: "80px", sm: "60px", xs: "48px" },
              lineHeight: "100%",
              fontWeight: 650,
              color: theme.customColors.primary.prusianBlue,
              marginBottom: { lg: "48px", md: "36px", xs: "24px" },
            }}
            variant="h1"
          >
            About
          </Typography>
        </Grid>

        <Grid item lg={8} xs={12}>
          <Typography
            sx={{
              fontSize: { lg: "40px", md: "32px", xs: "26px" },
              lineHeight: 1.18,
              fontWeight: 650,
              color: theme.customColors.primary.prusianBlue,
              maxWidth: "980px",
              marginBottom: "28px",
            }}
          >
            The Data Provenance Initiative builds public measurement
            infrastructure for understanding AI data, model ecosystems, and
            real-world AI use.
          </Typography>
          <Typography
            sx={{
              fontSize: { lg: "22px", md: "20px", xs: "18px" },
              lineHeight: 1.5,
              fontWeight: 450,
              color: theme.customColors.primary.prusianBlue,
              maxWidth: "900px",
              marginBottom: "24px",
            }}
          >
            We are a volunteer collective of AI researchers conducting
            large-scale audits of the systems and resources that shape modern
            AI. Our work began with training data provenance: tracing datasets
            from origin to creation, cataloging sources, licenses, creators, and
            documentation practices. It now also covers web consent, open model
            markets, concentration of power, and how people use AI systems in
            practice.
          </Typography>
          <Typography
            sx={{
              fontSize: { lg: "22px", md: "20px", xs: "18px" },
              lineHeight: 1.5,
              fontWeight: 450,
              color: theme.customColors.primary.prusianBlue,
              maxWidth: "900px",
            }}
          >
            You can download the dataset audit directly from the{" "}
            <Link
              to={DATA_REPO_LINK}
              target="_blank"
              rel="noreferrer"
              style={{
                color: theme.customColors.primary.lion,
                textDecoration: "underline",
              }}
            >
              Data Provenance Collection
            </Link>
            . To contribute or discuss the work, contact the organizers at{" "}
            <Link
              to={`mailto:${CONTACT_EMAIL}`}
              style={{
                color: theme.customColors.primary.lion,
                wordBreak: "break-word",
                textDecoration: "underline",
              }}
            >
              {CONTACT_EMAIL}
            </Link>
            .
          </Typography>
        </Grid>

        <Grid item lg={4} xs={12}>
          <Card
            sx={{
              backgroundColor: theme.customColors.primary.prusianBlue,
              borderRadius: "8px",
              color: theme.customColors.primary.aliceBlue,
              height: "100%",
            }}
          >
            <CardContent sx={{ padding: { lg: "32px", xs: "24px" } }}>
              <Typography
                sx={{
                  color: theme.customColors.supplementary.bluish,
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: "18px",
                }}
              >
                Measurement systems
              </Typography>
              {researchPrograms.map((program) => (
                <Box
                  key={program.title}
                  sx={{
                    borderTop: "1px solid rgba(222, 243, 255, 0.18)",
                    padding: "18px 0",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "20px", fontWeight: 750, lineHeight: 1.15 }}
                  >
                    {program.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme.customColors.supplementary.bluish,
                      fontSize: "15px",
                      lineHeight: 1.35,
                      marginTop: "8px",
                    }}
                  >
                    {program.metrics
                      .map((metric) => `${metric.value} ${metric.label}`)
                      .join(" / ")}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item md={12} xs={12}>
          <Box
            sx={{
              borderTop: `1px solid ${theme.customColors.primary.prusianBlue}`,
              borderBottom: `1px solid ${theme.customColors.primary.prusianBlue}`,
              marginTop: { lg: "64px", xs: "38px" },
              padding: { lg: "42px 0", xs: "30px 0" },
            }}
          >
            <Grid container spacing={{ lg: 5, xs: 3 }}>
              <Grid item lg={5} xs={12}>
                <Typography
                  sx={{
                    color: theme.customColors.supplementary.horizon,
                    fontSize: "13px",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    marginBottom: "12px",
                  }}
                >
                  Impact and community
                </Typography>
                <Typography
                  sx={{
                    color: theme.customColors.primary.prusianBlue,
                    fontSize: { lg: "42px", xs: "32px" },
                    fontWeight: 750,
                    lineHeight: 1.05,
                    marginBottom: "18px",
                  }}
                  variant="h2"
                >
                  A public reference point for AI evidence
                </Typography>
                <Typography
                  sx={{
                    color: theme.customColors.primary.prusianBlue,
                    fontSize: { lg: "19px", xs: "17px" },
                    lineHeight: 1.5,
                    maxWidth: "720px",
                  }}
                >
                  DPI is designed to make otherwise fragmented AI infrastructure
                  measurable: what data systems use, what web publishers permit,
                  who participates in model markets, and how people actually use
                  AI tools.
                </Typography>
              </Grid>
              <Grid item lg={7} xs={12}>
                <Grid container spacing={2}>
                  {communitySignals.map((signal) => (
                    <Grid item md={4} xs={12} key={signal.label}>
                      <Box
                        sx={{
                          borderTop: `4px solid ${theme.customColors.primary.lion}`,
                          height: "100%",
                          paddingTop: "18px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: "20px",
                            fontWeight: 750,
                            lineHeight: 1.15,
                            marginBottom: "10px",
                          }}
                        >
                          {signal.label}
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: "16px",
                            lineHeight: 1.45,
                          }}
                        >
                          {signal.text}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item md={12} xs={12}>
          <Box
            sx={{
              marginTop: { lg: "64px", xs: "36px" },
              marginBottom: { lg: "54px", xs: "32px" },
            }}
          >
            <Typography
              sx={{
                fontSize: { lg: "56px", md: "48px", sm: "40px", xs: "34px" },
                lineHeight: "100%",
                fontWeight: 650,
                color: theme.customColors.primary.prusianBlue,
                marginBottom: { lg: "28px", xs: "20px" },
              }}
              variant="h2"
            >
              Research Programs
            </Typography>
            <Grid container spacing={2.5}>
              {researchPrograms.map((program) => (
                <Grid item lg={3} sm={6} xs={12} key={program.title}>
                  <Card
                    sx={{
                      border: `1px solid ${theme.customColors.primary.prusianBlue}`,
                      borderRadius: "8px",
                      boxShadow: "none",
                      height: "100%",
                    }}
                  >
                    <CardContent
                      sx={{
                        padding: "22px",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.customColors.supplementary.horizon,
                          fontSize: "12px",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          marginBottom: "14px",
                        }}
                      >
                        {program.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          fontSize: "24px",
                          fontWeight: 750,
                          lineHeight: 1.1,
                          marginBottom: "14px",
                        }}
                      >
                        {program.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          fontSize: "16px",
                          lineHeight: 1.45,
                          marginBottom: "18px",
                        }}
                      >
                        {program.description}
                      </Typography>
                      {program.comingSoon ? (
                        <Typography
                          sx={{
                            color: theme.customColors.supplementary.horizon,
                            fontSize: "16px",
                            fontWeight: 700,
                            marginTop: "auto",
                          }}
                        >
                          {program.cta}
                        </Typography>
                      ) : (
                        <Link
                          to={program.href}
                          target={program.external ? "_blank" : undefined}
                          rel={program.external ? "noreferrer" : undefined}
                          style={{
                            color: theme.customColors.primary.lion,
                            fontSize: "16px",
                            fontWeight: 700,
                            marginTop: "auto",
                            textDecoration: "none",
                          }}
                        >
                          {program.cta}
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item md={12} xs={12}>
          <Box sx={{ marginBottom: { lg: "70px", xs: "44px" } }}>
            <Typography
              sx={{
                fontSize: { lg: "56px", md: "48px", sm: "40px", xs: "34px" },
                lineHeight: "100%",
                fontWeight: 650,
                color: theme.customColors.primary.prusianBlue,
                marginBottom: { lg: "22px", xs: "16px" },
              }}
              variant="h2"
            >
              Recent Work
            </Typography>
            <Box>
              {researchOutputs.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { md: "120px 1fr 150px", xs: "1fr" },
                    gap: { md: "24px", xs: "8px" },
                    borderBottom: `1px solid ${theme.customColors.primary.prusianBlue}`,
                    padding: { md: "24px 0", xs: "22px 0" },
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.customColors.supplementary.horizon,
                      fontSize: { md: "24px", xs: "18px" },
                      fontWeight: 750,
                      lineHeight: 1,
                    }}
                  >
                    {item.year}
                  </Typography>
                  <Box>
                    {item.comingSoon ? (
                      <Typography
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          fontSize: { lg: "28px", md: "24px", xs: "21px" },
                          fontWeight: 750,
                          lineHeight: 1.2,
                          marginBottom: "8px",
                        }}
                      >
                        {item.title}
                      </Typography>
                    ) : (
                      <Link
                        to={item.href}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noreferrer" : undefined}
                      >
                        <Typography
                          sx={{
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: { lg: "28px", md: "24px", xs: "21px" },
                            fontWeight: 750,
                            lineHeight: 1.2,
                            marginBottom: "8px",
                            ":hover": {
                              color: theme.customColors.primary.lion,
                            },
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Link>
                    )}
                    <Typography
                      sx={{
                        color: theme.customColors.primary.prusianBlue,
                        fontSize: { md: "18px", xs: "16px" },
                        lineHeight: 1.45,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: theme.customColors.primary.lion,
                      fontSize: "15px",
                      fontWeight: 750,
                      textAlign: { md: "right", xs: "left" },
                    }}
                  >
                    {item.tag}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item md={12} xs={12}>
          <Typography
            sx={{
              borderTop: `1px solid ${theme.customColors.supplementary.grey}`,
              color: theme.customColors.primary.prusianBlue,
              fontSize: { lg: "16px", xs: "14px" },
              fontStyle: "italic",
              fontWeight: 450,
              lineHeight: 1.45,
              paddingTop: "22px",
            }}
          >
            Note: We collect self-reported licenses from the papers and
            repositories that released these datasets and categorize them
            according to our best efforts as a volunteer research and
            transparency initiative. Information provided by our work and
            outputs of the Data Provenance Initiative does not constitute legal
            advice; it is provided for general informational purposes.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default About;
